<template>
  <div class="modal fade" :id="''+id" data-bs-backdrop="static" tabindex="-1" role="dialog" aria-labelledby="staticBackdrop" aria-hidden="true">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ $t('access.configAccess.title') + ': ' +  data.name }}</h5>
          <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" @click="closeModal" aria-label="Close">
              <span class="svg-icon svg-icon-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)"
                        fill="currentColor"/>
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)"
                        fill="currentColor"/>
                  </svg>
              </span>
          </div>
        </div>
        <div class="modal-body scroll-y" style="max-height: 75vh;">
          <div class="row">
            <div class="col-12 mb-4">
              <div class="d-flex justify-content-between align-items-center">
                <Card class="card shadow-sm border border-gray-300 p-0">
                  <div class="h-50px p-0 px-3 py-2 d-flex align-items-center">
                    <SwitchInput left
                                 :label="$t('access.configAccess.authoriseAccess')"
                                 :modelValue="formData.enabled"
                                 @update:modelValue="toggleAuthorizeStatus"
                                 :is-loading="updatingAccessStatus"
                    />
                  </div>
                </Card>
                <RequiredText :text="$t('access.configAccess.authoriseAccessWarn')"/>
              </div>
            </div>
            <div class="col-12 mb-4">
              <UserAccessMethodProcessConfig
                  :id="doorOrPerimeter"
                  :is-door="data.isDoor"
                  :currentConfig="formData"
                  @callback="$emit('callback')" />
            </div>
            <div class="col-12">
              <Card class="card shadow-sm border border-gray-300">
                <template #cardHeader>
                  <div class="card-header">
                    <h4 class="card-title">{{ $t('access.configAccess.accessHours') }}</h4>
                    <div class="card-toolbar">
                      <Button
                          type="button"
                          btn-class="btn btn-sm btn-primary"
                          size="7px 30px"
                          :btn-text="canEditHours ? 'button.save' : 'button.edit'"
                          :is-loading="updatingHour"
                          @click.prevent="handleHoursAction"
                      />
                    </div>
                  </div>
                </template>
                <template #cardBody>
                  <div class="card-body p-0 p-2">
                    <AccessHours
                        :canEditHours="canEditHours"
                        :hours="formData.hours" />
                  </div>
                </template>
              </Card>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <Button type="cancel" id="cancel_btn" btn-class="btn-sm me-3" size="7px 30px" btn-text="button.close" @click="closeModal"></Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, defineProps, watch } from 'vue'
import {useStore} from "vuex";
import RequiredText from "@/components/RequiredText"
import Button from "@/components/Button.vue"
import Card from "@/components/Card.vue";
import SwitchInput from "@/components/SwitchInput.vue";
import {useLocales} from "@/composables/locales";
import AccessHours from "@/Modules/Access/views/Config/components/AccessHours.vue";
import UserAccessMethodProcessConfig from "@/Modules/Access/views/Config/components/UserAccessMethodProcessConfig.vue";

const { locale } = useLocales()

const store = useStore()

const props = defineProps({
  data: {
    type: Object,
    default: {
      id: '',
      name: '',
      perimeter: '',
      door: '',
      user: '',
      group: '',
      datetime_frames: [],
      hours: [],
      enabled: false,
      accessMethods: []
    },
    required: true
  },
  id: {
    type: String,
    required: true
  },
  parent: {
    type: String,
    required: false
  }
})

const emits = defineEmits(['callback'])

const formData = ref(props.data)
const isLoading = ref(false)
const canEditHours = ref(false)
const updatingHour = ref(false)
const updatingAccessStatus = ref(false)


const accessMethods = computed(() => store.getters['methods/getMethods'])
const doorOrPerimeter = computed(() => props.data.isDoor ? props.data.door : props.data.perimeter)

watch(() => props.data, () => {
  formData.value = {...props.data}
})

function closeModal(){
  if(props.parent){
    $('#'+props.parent).modal('toggle')
  }
  $('#'+props.id).modal('toggle')
  formData.value = {
    id: '',
    accessMethods: [],
    hours: []
  }
  canEditHours.value = false
}


function toggleEditHours() {
  canEditHours.value = !canEditHours.value
}

function handleHoursAction() {
  canEditHours.value
      ? setHoursConfig()
      : toggleEditHours()
}

function setHoursConfig(){
  updatingHour.value = true

  const isDoor = props.data.isDoor
  let data = {
    doors: isDoor ? [props.data.door] : null,
    perimeters: !isDoor ? [props.data.perimeter] : null,
    users: props.data.user ? [props.data.user] : null,
    groups: props.data.group ? [props.data.group] : null,
    datetime_frames: formData.value.hours
  }


  store.dispatch('access/setAccessHours', data)
      .then((response) => {
        emits('callback')
        window.toastr['success'](i18n.global.t('notification.config_updated'))
        toggleEditHours()
      })
      .catch()
      .finally(() => updatingHour.value = false)
}

function toggleAuthorizeStatus() {
  updatingAccessStatus.value = true
  let data = {
    perimeters: formData.value.perimeter ? [formData.value.perimeter] : [],
    doors: formData.value.door ? [formData.value.door] : [],
    users: formData.value.user ? [formData.value.user] : [],
    groups: formData.value.group ? [formData.value.group] : [],
    enabled: !formData.value.enabled
  }

  store.dispatch('access/setAccess', data)
      .then((response) => {
        formData.value.enabled = !formData.value.enabled
        emits('callback')
        window.toastr['success'](i18n.global.t('notification.config_updated'))
      })
      .catch()
      .finally(() =>  updatingAccessStatus.value = false)
}


</script>